import { createElement, forwardRef, type ForwardRefExoticComponent, type RefAttributes, type SVGProps } from "react";
import { SHAPES } from "./set.generated";

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "ref"> {
    /** Width and height in px (a number) or any CSS length. Default 24. */
    size?: number | string;
    /** Stroke width in the 24-unit grid. Default 1.75. */
    strokeWidth?: number | string;
    /** Draw the stroke at the same pixel width however large the icon is. */
    absoluteStrokeWidth?: boolean;
}

/** What `import { Mail } from "@fg/icons"` gives: a component, and the type to hold one. */
export type IconComponent = ForwardRefExoticComponent<IconProps & RefAttributes<SVGSVGElement>>;
export type LucideIcon = IconComponent;

/**
 * One Fowlguard icon. Strokes use the current text colour; the accent parts use `--fg-icon-accent`
 * when a theme sets it and the text colour when not. The soft tint under the strokes is the accent at low opacity.
 */
export function make(key: string, displayName: string): IconComponent {
    const shape = SHAPES[key];
    if (!shape) throw new Error(`No Fowlguard icon named ${key}`);
    const C = forwardRef<SVGSVGElement, IconProps>(function FgIcon({ size = 24, strokeWidth = 1.75, absoluteStrokeWidth, color, children, ...rest }, ref) {
        const sw = absoluteStrokeWidth ? (Number(strokeWidth) * 24) / Number(size) : strokeWidth;
        const accent = "var(--fg-icon-accent, currentColor)";
        return createElement(
            "svg",
            {
                ref, xmlns: "http://www.w3.org/2000/svg", width: size, height: size, viewBox: "0 0 24 24", fill: "none",
                stroke: color ?? "currentColor", strokeWidth: sw, strokeLinecap: "round", strokeLinejoin: "round",
                "aria-hidden": rest["aria-label"] ? undefined : true, "data-fg-icon": key, ...rest
            },
            ...shape.tint.map((d, i) => createElement("path", { key: `t${i}`, d, fill: accent, fillOpacity: 0.16, stroke: "none" })),
            ...shape.body.map((d, i) => createElement("path", { key: `b${i}`, d })),
            ...shape.accent.map((d, i) => createElement("path", { key: `a${i}`, d, stroke: color ?? accent })),
            children
        );
    });
    C.displayName = displayName;
    return C;
}
